import React, { createContext, useContext, useEffect, useState } from 'react'
import type { CSSProperties } from 'react'

export type AppTheme = 'edc' | 'primersoft' | 'dark'

/**
 * Outlined “ghost” actions aligned with GlobalHeader Sign Out in dark mode,
 * and the same visual language on light cards (EDC / Primersoft).
 */
export function portalAccentGhostButtonStyle(theme: AppTheme, primary: string): CSSProperties {
	if (theme === 'dark') {
		return {
			backgroundColor: 'rgba(56, 189, 248, 0.08)',
			borderColor: primary,
			color: primary,
			fontWeight: 600,
			borderRadius: 8,
		}
	}
	if (theme === 'edc') {
		return {
			backgroundColor: 'rgba(37, 74, 137, 0.08)',
			borderColor: primary,
			color: primary,
			fontWeight: 600,
			borderRadius: 8,
		}
	}
	return {
		backgroundColor: 'rgba(91, 95, 102, 0.12)',
		borderColor: primary,
		color: primary,
		fontWeight: 600,
		borderRadius: 8,
	}
}

interface ThemeContextValue {
	theme: AppTheme
	setTheme: (t: AppTheme) => void
	isDarkMode: boolean
}

const STORAGE_KEY = 'app-theme'

const ThemeContext = createContext<ThemeContextValue>({
	theme: 'edc',
	setTheme: () => {},
	isDarkMode: false,
})

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [theme, setThemeState] = useState<AppTheme>(() => {
		const saved = localStorage.getItem(STORAGE_KEY)
		return (saved as AppTheme) || 'edc'
	})

	const setTheme = (t: AppTheme) => {
		setThemeState(t)
		localStorage.setItem(STORAGE_KEY, t)
	}

	useEffect(() => {
		document.body.setAttribute('data-theme', theme)
		if (theme === 'dark') {
			document.body.classList.add('dark-mode')
		} else {
			document.body.classList.remove('dark-mode')
		}
	}, [theme])

	return (
		<ThemeContext.Provider value={{ theme, setTheme, isDarkMode: theme === 'dark' }}>
			{children}
		</ThemeContext.Provider>
	)
}

export const useTheme = () => useContext(ThemeContext)

/** Returns the active theme's primary + charcoal colors for use in inline styles */
export const useThemeColors = () => {
	const { theme, isDarkMode } = useTheme()

	const lightColors = {
		bg: '#ffffff',
		subBg: '#f8fafc',
		chartBg: '#fafbfc',
		border: '#f1f5f9',
		line: '#e2e8f0',
		text: '#0f172a',
		textSecondary: '#64748b',
		textTertiary: '#94a3b8',
		cardShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
		cardHoverShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
	}

	const darkColors = {
		bg: '#0f172a', // Card background
		subBg: '#1e293b', // Inner section background (L2/L3) / Filter bar
		chartBg: '#111827', // Main page/container background
		border: '#1e293b',
		line: '#334155',
		text: '#f8fafc',
		textSecondary: '#cbd5e1',
		textTertiary: '#94a3b8',
		cardShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
		cardHoverShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)',
	}

	const base = isDarkMode ? darkColors : lightColors

	if (theme === 'dark') {
		return {
			...base,
			primary: '#38bdf8',
			charcoal: '#1e293b',
			gradient: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
		}
	}

	return theme === 'primersoft'
		? {
				...base,
				primary: '#5b5f66',
				charcoal: '#414042',
				gradient: 'linear-gradient(135deg, #5b5f66 0%, #414042 100%)',
			}
		: {
				...base,
				primary: '#254a89',
				charcoal: '#254a89',
				gradient: 'linear-gradient(135deg, #254a89 0%, #1a3560 100%)',
			}
}
